import axios from 'axios'
import { toast } from 'react-toastify'
import {
  getHrAccessToken,
  handleUnauthorized,
  setAuthenticationCookies,
  shouldEndSessionForResponse,
} from 'services/session/authSession'

const API_BASE_URL = process.env.REACT_APP_API_URL
const OFFLINE_ERROR_MESSAGE = 'Connection lost. Please reconnect and try again.'
const request = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: 'application/json',
  },
})

request.interceptors.request.use(
  async (config) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const offlineError = new Error(OFFLINE_ERROR_MESSAGE)
      offlineError.isOffline = true
      offlineError.status = 0
      offlineError.data = { message: OFFLINE_ERROR_MESSAGE }
      toast.error(OFFLINE_ERROR_MESSAGE)
      return Promise.reject(offlineError)
    }

    const hr_access_token = getHrAccessToken()
    if (hr_access_token) {
      config.headers['Authorization'] = `Bearer ${hr_access_token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

request.interceptors.response.use(
  (response) => {
    if (response.data.message === 'Authentication successful') {
      setAuthenticationCookies({
        proAccessToken: response.data.data.access_token,
        hrAccessToken: response.data.data.hr_access_token,
        hrExpiresAt: response.data.data.hr_access_token_expires_at,
      })
    }

    return response
  },

  (error) => {
    if (error?.isOffline || (!error?.response && typeof navigator !== 'undefined' && navigator.onLine === false)) {
      const offlineError = error?.isOffline ? error : new Error(OFFLINE_ERROR_MESSAGE)
      offlineError.isOffline = true
      offlineError.status = 0
      offlineError.data = { message: OFFLINE_ERROR_MESSAGE }
      return Promise.reject(offlineError)
    }

    // If response data type is blob
    if (
      error.request?.responseType === 'blob' &&
      error.response?.data instanceof Blob &&
      error.response?.data.type &&
      error.response.data.type.toLowerCase().indexOf('json') !== -1
    ) {
      return new Promise((_, reject) => {
        let reader = new FileReader()
        reader.onload = () => {
          try {
            error.response.data = JSON.parse(reader.result)
          } catch (parseError) {
            reject(error.response)
            return
          }

          if (shouldEndSessionForResponse(error.response.status)) {
            handleUnauthorized(error)
          }

          reject(error.response)
        }

        reader.onerror = () => {
          reject(error)
        }

        reader.readAsText(error.response.data)
      })
    }

    if (error.response) {
      const { status } = error.response

      if (shouldEndSessionForResponse(status)) {
        handleUnauthorized(error)
      } else if (status === 403) {
        console.log('You dont have permissions to perform this task')
      } else if (status === 500) {
        console.log('Server Error')
      }

      return Promise.reject(error.response)
    }

    return Promise.reject(error)
  }
)

export function getData(response) {
  return response.data
}

export default request
